import { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";

// CSS deliberately mirrors Employees.js/Payroll.js/DocumentExpiry.js's
// tokens and .adm-* classes 1:1 so this reads as the same module.
const CSS = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg: #f0f4fc; --surface: #ffffff; --surface2: #f4f8ff;
  --border: #c0d0eb; --border2: #d5e3f5;
  --accent: #0284c7; --text: #0d1a36; --text2: #2a4a78; --muted: #6080aa;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color-scheme: light;
}
body { background: var(--bg); color: var(--text); }
.adm-topbar { position: sticky; top: 0; z-index: 100; height: 52px; display: flex; align-items: center; background: rgba(240,244,252,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border2); }
.adm-back { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; background: none; border: none; border-right: 1px solid var(--border2); color: var(--text2); cursor: pointer; font-size: 16px; flex-shrink: 0; transition: color .15s, background .15s; }
.adm-back:hover { color: var(--text); background: var(--surface); }
.adm-brand { display: flex; align-items: center; gap: 10px; padding: 0 20px; flex: 1; }
.adm-brand-mark { width: 26px; height: 26px; border-radius: 6px; background: linear-gradient(135deg, #0284c7, #2563eb); display: flex; align-items: center; justify-content: center; font-size: 13px; }
.adm-brand-label { font-size: 14px; font-weight: 600; color: var(--text); }
.adm-avatar { margin-right: 16px; width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, #2563eb, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0; }
.adm-tabs { display: flex; align-items: center; gap: 2px; padding: 0 20px; border-bottom: 1px solid var(--border2); background: var(--surface); overflow-x: auto; }
.adm-tab { padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--muted); background: none; border: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s; font-family: inherit; white-space: nowrap; }
.adm-tab:hover { color: var(--text2); }
.adm-tab.active { color: var(--accent); font-weight: 700; border-bottom-color: var(--accent); }
.adm-main { max-width: 1200px; margin: 0 auto; padding: 28px 24px 80px; }
.adm-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 24px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(13,26,54,.04), 0 4px 12px rgba(13,26,54,.05); }
.adm-card-title { font-size: 14px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
.adm-card-desc { font-size: 12px; color: var(--muted); margin-bottom: 16px; line-height: 1.5; }
.adm-error { padding: 10px 14px; border-radius: 7px; background: rgba(248,113,113,.08); border: 1px solid rgba(248,113,113,.2); color: #dc2626; font-size: 13px; margin-bottom: 14px; }
.adm-toast { position: fixed; top: 68px; right: 20px; z-index: 300; padding: 12px 16px; border-radius: 8px;
  background: #ffffff; border: 1px solid rgba(16,185,129,.3); color: #059669; font-size: 13px; font-weight: 500;
  box-shadow: 0 4px 16px rgba(13,26,54,.12), 0 1px 3px rgba(13,26,54,.08); max-width: 360px;
  display: flex; align-items: center; gap: 8px; animation: admToastIn .25s ease-out; }
@keyframes admToastIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
.adm-toast-dot { width: 8px; height: 8px; border-radius: 50%; background: #059669; flex-shrink: 0; }
.adm-skeleton-bar { height: 13px; border-radius: 4px; background: linear-gradient(90deg, var(--border2) 25%, var(--surface2) 42%, var(--border2) 63%); background-size: 400% 100%; animation: admShimmer 1.4s ease infinite; }
@keyframes admShimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }
.adm-skeleton-row { display: grid; grid-template-columns: 1fr 1.4fr 1fr 1fr 1fr; gap: 20px; align-items: center; padding: 13px 12px; border-bottom: 1px solid var(--border2); }
.adm-empty { font-size: 13px; color: var(--muted); padding: 28px 20px; text-align: center; border: 1px dashed var(--border2); border-radius: 8px; background: var(--surface2); }
.adm-input { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; transition: border-color .15s; width: 100%; }
.adm-input:focus { border-color: var(--accent); }
.adm-input:disabled { opacity: .5; cursor: not-allowed; background: var(--border2); }
.adm-select { background: var(--surface2); border: 1px solid var(--border2); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); outline: none; font-family: inherit; cursor: pointer; width: 100%; }
.adm-field { display: flex; flex-direction: column; gap: 6px; }
.adm-label { font-size: 11px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: var(--muted); }
.adm-form-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
@media(max-width: 760px) { .adm-form-grid { grid-template-columns: 1fr; } }
.adm-checkbox-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text2); }
.adm-action-btn { background: none; border: 1px solid var(--border); border-radius: 6px; padding: 5px 11px; font-size: 12px; color: var(--text2); cursor: pointer; font-family: inherit; transition: border-color .15s, color .15s; white-space: nowrap; }
.adm-action-btn:hover { border-color: var(--accent); color: var(--accent); }
.adm-action-btn.danger:hover { border-color: #dc2626; color: #dc2626; }
.adm-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; background: none; border: 1px solid var(--border); border-radius: 6px; color: var(--text2); cursor: pointer; transition: border-color .15s, color .15s, background .15s; }
.adm-icon-btn:hover { border-color: var(--accent); color: var(--accent); background: var(--surface2); }
.adm-icon-btn.danger:hover { border-color: #dc2626; color: #dc2626; background: rgba(220,38,38,.06); }
.cdoc-row-actions { display: flex; gap: 6px; }
.adm-submit { padding: 10px 24px; border-radius: 7px; background: linear-gradient(135deg, #0284c7, #2563eb); border: none; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; transition: opacity .15s, box-shadow .15s; box-shadow: 0 2px 8px rgba(2,132,199,.25); }
.adm-submit:hover { opacity: .9; box-shadow: 0 4px 14px rgba(2,132,199,.35); }
.adm-submit:disabled { opacity: .5; cursor: not-allowed; }
.adm-table-wrap { overflow-x: auto; }
.adm-table { width: 100%; border-collapse: collapse; min-width: 760px; }
.adm-table th { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--muted); padding: 0 14px 10px; text-align: left; border-bottom: 1px solid var(--border2); white-space: nowrap; }
.adm-table td { padding: 12px 14px; font-size: 13px; color: var(--text2); border-bottom: 1px solid var(--border2); vertical-align: middle; }
.adm-table tr:last-child td { border-bottom: none; }
.adm-table tr:hover td { background: var(--surface2); }
.adm-table .doc-title { color: var(--text); font-weight: 600; }
.adm-table .doc-code { color: var(--muted); font-variant-numeric: tabular-nums; }
.cdoc-days { font-variant-numeric: tabular-nums; }

.cdoc-page-head { display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 10px; margin-bottom: 20px; }
.cdoc-page-head h1 { font-family: inherit; font-size: 20px; font-weight: 700; color: var(--text); }
.cdoc-page-head p { font-size: 12.5px; color: var(--muted); margin-top: 3px; }

.cdoc-modal-overlay { position: fixed; inset: 0; z-index: 400; background: rgba(13,26,54,.45); backdrop-filter: blur(2px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: cdocFadeIn .15s ease-out; }
@keyframes cdocFadeIn { from { opacity: 0; } to { opacity: 1; } }
.cdoc-modal { background: var(--surface); border-radius: 14px; padding: 26px 28px 24px; max-width: 420px; width: 100%; box-shadow: 0 24px 60px rgba(13,26,54,.28), 0 2px 8px rgba(13,26,54,.1); animation: cdocModalIn .18s ease-out; }
@keyframes cdocModalIn { from { opacity: 0; transform: translateY(10px) scale(.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
.cdoc-modal-icon { width: 44px; height: 44px; border-radius: 50%; background: rgba(180,83,9,.1); display: flex; align-items: center; justify-content: center; color: #b45309; margin-bottom: 16px; }
.cdoc-modal-title { font-size: 15.5px; font-weight: 700; color: var(--text); margin-bottom: 8px; }
.cdoc-modal-body { font-size: 13.5px; color: var(--text2); line-height: 1.6; margin-bottom: 24px; }
.cdoc-modal-body strong { color: var(--text); }
.cdoc-modal-actions { display: flex; justify-content: flex-end; gap: 10px; }
.cdoc-modal-confirm { background: linear-gradient(135deg, #b45309, #d97706); box-shadow: 0 2px 8px rgba(180,83,9,.25); }
.cdoc-modal-confirm:hover { box-shadow: 0 4px 14px rgba(180,83,9,.35); }

.cdoc-history-row td { background: var(--surface2); padding: 20px 22px; border-bottom: 1px solid var(--border2); }
.cdoc-history-panel { display: flex; flex-direction: column; gap: 20px; }
.cdoc-history-section-title { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); margin-bottom: 10px; }
.cdoc-renew-form { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.cdoc-renew-field { display: flex; flex-direction: column; gap: 6px; min-width: 150px; }
.cdoc-version-list { display: flex; flex-direction: column; gap: 8px; }
.cdoc-version-row { display: grid; grid-template-columns: 50px 84px 108px 1fr 1fr 1fr 1fr auto; gap: 10px; align-items: center; padding: 10px 12px; background: var(--surface); border: 1px solid var(--border2); border-radius: 7px; font-size: 12.5px; color: var(--text2); }
.cdoc-version-num { font-weight: 700; color: var(--text); font-variant-numeric: tabular-nums; }
.cdoc-version-badge { font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; padding: 3px 9px; border-radius: 20px; white-space: nowrap; text-align: center; }
.cdoc-version-badge.current { background: rgba(2,132,199,.1); color: var(--accent); }
.cdoc-version-badge.historical { background: var(--border2); color: var(--muted); }
.cdoc-version-meta { color: var(--muted); font-size: 11.5px; }
.cdoc-pagination { display: flex; justify-content: center; align-items: center; gap: 14px; margin-top: 16px; }
.cdoc-pagination-page { font-size: 12px; color: var(--muted); font-variant-numeric: tabular-nums; }

.cdoc-status-badge { display: inline-block; font-size: 10.5px; font-weight: 700; letter-spacing: .03em; padding: 4px 10px; border-radius: 20px; white-space: nowrap; }
.cdoc-status-badge.valid { background: rgba(5,150,105,.1); color: #059669; }
.cdoc-status-badge.expiring { background: rgba(180,83,9,.1); color: #b45309; }
.cdoc-status-badge.expired { background: rgba(220,38,38,.1); color: #dc2626; }
.cdoc-status-badge.noexpiry { background: rgba(2,132,199,.1); color: #0284c7; }
.cdoc-status-badge.review { background: rgba(124,58,237,.1); color: #7c3aed; }
.cdoc-status-badge.missing { background: rgba(220,38,38,.08); color: #dc2626; }
.cdoc-status-badge.norecords { background: var(--border2); color: var(--muted); }

.cdoc-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; margin-bottom: 22px; }
.cdoc-stat-card { display: flex; flex-direction: column; gap: 4px; background: var(--surface); border: 1px solid var(--border); border-left: 3px solid var(--border); border-radius: 10px; padding: 15px 18px; cursor: pointer; text-align: left; font-family: inherit; box-shadow: 0 1px 2px rgba(13,26,54,.04); transition: box-shadow .15s, transform .15s, border-color .15s; }
.cdoc-stat-card:hover { box-shadow: 0 6px 16px rgba(13,26,54,.09); transform: translateY(-1px); }
.cdoc-stat-card .count { font-size: 26px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; color: var(--text); }
.cdoc-stat-card .label { font-size: 12px; font-weight: 600; color: var(--muted); }
.cdoc-stat-card.expired { border-left-color: #dc2626; }
.cdoc-stat-card.expired .count { color: #dc2626; }
.cdoc-stat-card.expiring { border-left-color: #b45309; }
.cdoc-stat-card.expiring .count { color: #b45309; }
.cdoc-stat-card.review { border-left-color: #7c3aed; }
.cdoc-stat-card.review .count { color: #7c3aed; }
.cdoc-stat-card.ok { border-left-color: #059669; }
.cdoc-stat-card.ok .count { color: #059669; }
.cdoc-stat-card.noexpiry { border-left-color: #0284c7; }
.cdoc-stat-card.noexpiry .count { color: #0284c7; }

.cdoc-subtabs { display: inline-flex; gap: 3px; margin-bottom: 20px; background: var(--surface2); border: 1px solid var(--border2); border-radius: 10px; padding: 4px; }
.cdoc-subtab { padding: 8px 20px; font-size: 13px; font-weight: 600; color: var(--text2); background: none; border: none; border-radius: 7px; cursor: pointer; font-family: inherit; transition: background .15s, color .15s; }
.cdoc-subtab:hover { color: var(--text); }
.cdoc-subtab.active { background: var(--surface); color: var(--accent); box-shadow: 0 1px 3px rgba(13,26,54,.1); }

.cdoc-diagnostic-action { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
`;

function IconArrowLeft(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
  );
}

function IconEye(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16Z" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function IconHistory(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
      <path d="M12 7v5l4 2" />
    </svg>
  );
}

function IconWarning(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

const STATUS_META = {
  valid: { label: "Valid", cls: "valid" },
  expiring_soon: { label: "Expiring Soon", cls: "expiring" },
  expired: { label: "Expired", cls: "expired" },
  no_expiry: { label: "No Expiry", cls: "noexpiry" },
  review_required: { label: "Review Required", cls: "review" },
  missing: { label: "Missing", cls: "missing" },
  no_records: { label: "No Records", cls: "norecords" },
  sent: { label: "Sent", cls: "valid" },
  failed: { label: "Failed", cls: "expired" },
  skipped_no_recipients: { label: "No Recipients", cls: "norecords" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "norecords" };
  return <span className={`cdoc-status-badge ${meta.cls}`}>{meta.label}</span>;
}

const BLANK_FORM = { document_type_id: "", record_title: "", issue_date: "", expiry_date: "", no_expiry: false };
const DOC_PAGE_SIZE = 25;

export default function CompanyDocuments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [types, setTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docPage, setDocPage] = useState(1);
  const [docTotal, setDocTotal] = useState(0);
  const [docTotalPages, setDocTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // debounced from searchInput
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [statusSummary, setStatusSummary] = useState({});
  const [showAllCategories, setShowAllCategories] = useState(false); // collapsed to in-use categories by default
  const [activeSubTab, setActiveSubTab] = useState("documents"); // "documents" | "categories" | "reminders" | "credentials"

  const [credentials, setCredentials] = useState([]);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [showAddCredentialForm, setShowAddCredentialForm] = useState(false);
  const [credentialForm, setCredentialForm] = useState({ title: "", username: "", password: "", notes: "" });
  const [savingCredential, setSavingCredential] = useState(false);
  const [editingCredentialId, setEditingCredentialId] = useState(null);
  const [editCredentialForm, setEditCredentialForm] = useState({ title: "", username: "", password: "", notes: "" });
  const [savingCredentialEdit, setSavingCredentialEdit] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState({}); // credential id -> plaintext, transient/client-only
  const [revealingId, setRevealingId] = useState(null);
  const [showArchivedCredentials, setShowArchivedCredentials] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [remindersLog, setRemindersLog] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(true);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [previewDue, setPreviewDue] = useState(null); // null = not yet checked; [] = checked, nothing due
  const [previewing, setPreviewing] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK_FORM });
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0); // bumped on reset so the native <input type=file> actually clears its shown filename
  const [saving, setSaving] = useState(false);
  const [readingDates, setReadingDates] = useState(false);
  const [dateNeedsReview, setDateNeedsReview] = useState({ issue: false, expiry: false });
  const [dupWarning, setDupWarning] = useState(null); // { message } when the backend flags a near-duplicate title

  const [expandedDocId, setExpandedDocId] = useState(null);
  const [versionsByDoc, setVersionsByDoc] = useState({});
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [renewFile, setRenewFile] = useState(null);
  const [renewFileInputKey, setRenewFileInputKey] = useState(0); // same native-file-input reset trick as fileInputKey
  const [renewIssueDate, setRenewIssueDate] = useState("");
  const [renewExpiryDate, setRenewExpiryDate] = useState("");
  const [renewNoExpiry, setRenewNoExpiry] = useState(false);
  const [renewReadingDates, setRenewReadingDates] = useState(false);
  const [renewDateNeedsReview, setRenewDateNeedsReview] = useState({ issue: false, expiry: false });
  const [renewSaving, setRenewSaving] = useState(false);
  const [renewNotice, setRenewNotice] = useState(null); // { title, message, tone } shown after a renewal completes

  const [showAddType, setShowAddType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [newTypeAllowMultiple, setNewTypeAllowMultiple] = useState(true);
  const [newTypeRequireTitle, setNewTypeRequireTitle] = useState(false);
  const [savingType, setSavingType] = useState(false);

  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(""), 3000); };

  const loadTypes = useCallback(() => {
    api.get("/company-documents/types").then(r => setTypes(r.data.types || [])).catch(() => setTypes([]));
  }, []);

  const loadCategories = useCallback(() => {
    setLoadingCategories(true);
    api.get("/company-documents/categories")
      .then(r => {
        setCategories(r.data.categories || []);
        setStatusSummary(r.data.status_summary || {});
      })
      .catch(() => { setCategories([]); setStatusSummary({}); })
      .finally(() => setLoadingCategories(false));
  }, []);

  // Section 12: search/status/sort are all server-side query params now.
  // Section 22 (Performance): page/page_size too, so the list stays quick to load as it grows.
  const loadDocuments = useCallback(() => {
    setLoading(true);
    const params = { page: docPage, page_size: DOC_PAGE_SIZE };
    if (typeFilter) params.document_type_id = typeFilter;
    if (searchQuery) params.search = searchQuery;
    if (statusFilter) params.status = statusFilter;
    if (sortBy) params.sort = sortBy;
    api.get("/company-documents", { params })
      .then(r => {
        setDocuments(r.data.documents || []);
        setDocTotal(r.data.total ?? (r.data.documents || []).length);
        setDocTotalPages(r.data.total_pages || 1);
      })
      .catch(e => setError(e.response?.data?.detail || "Failed to load company documents."))
      .finally(() => setLoading(false));
  }, [typeFilter, searchQuery, statusFilter, sortBy, docPage]);

  // A filter change should always land back on page 1 -- otherwise a
  // narrower result set can leave the view stuck on a now-empty later page.
  useEffect(() => { setDocPage(1); }, [typeFilter, searchQuery, statusFilter, sortBy]);

  const loadRecipients = useCallback(() => {
    setLoadingRecipients(true);
    api.get("/company-documents/reminder-recipients")
      .then(r => setRecipients(r.data.recipients || []))
      .catch(() => setRecipients([]))
      .finally(() => setLoadingRecipients(false));
  }, []);

  const loadReminders = useCallback(() => {
    setLoadingReminders(true);
    api.get("/company-documents/reminders")
      .then(r => setRemindersLog(r.data.reminders || []))
      .catch(() => setRemindersLog([]))
      .finally(() => setLoadingReminders(false));
  }, []);

  const loadCredentials = useCallback(() => {
    setLoadingCredentials(true);
    api.get("/company-credentials")
      .then(r => setCredentials(r.data.credentials || []))
      .catch(() => setCredentials([]))
      .finally(() => setLoadingCredentials(false));
  }, []);

  useEffect(() => { loadTypes(); loadCategories(); loadRecipients(); loadReminders(); loadCredentials(); }, [loadTypes, loadCategories, loadRecipients, loadReminders, loadCredentials]);
  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  // Debounce free-text search so it doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const selectedType = types.find(t => t.id === form.document_type_id);

  const resetForm = () => { setForm({ ...BLANK_FORM }); setFile(null); setDateNeedsReview({ issue: false, expiry: false }); setFileInputKey(k => k + 1); };

  // OCR-01/02/03/04: reads only the issue/expiry dates from the file the
  // moment it's chosen, pre-fills them, and leaves them fully editable --
  // nothing is saved by this call, so a bad read never blocks the real
  // save (VAL-02).
  const handleFileChosen = async (chosenFile) => {
    setFile(chosenFile);
    setDateNeedsReview({ issue: false, expiry: false });
    if (!chosenFile) return;
    setReadingDates(true);
    try {
      const fd = new FormData();
      fd.append("file", chosenFile);
      const res = await api.post("/company-documents/ocr-dates", fd);
      setForm(p => ({
        ...p,
        issue_date: res.data.issue_date || p.issue_date,
        expiry_date: res.data.expiry_date || p.expiry_date,
      }));
      setDateNeedsReview({ issue: !res.data.issue_date, expiry: !res.data.expiry_date });
    } catch {
      // OCR failing here is never an error the user needs to see -- manual
      // entry always works (VAL-02); just leave the fields for them to fill in.
    } finally {
      setReadingDates(false);
    }
  };

  // Not a requirement-doc rule -- a soft duplicate-title warning added after
  // real testing showed the same director/asset getting re-added under a
  // typo'd name with no warning at all. confirmDuplicate=true means the user
  // already saw the warning and chose to save anyway; it never hard-blocks.
  const saveDocument = async (confirmDuplicate = false) => {
    if (!confirmDuplicate) {
      if (!form.document_type_id) { setError("Please select a Document Type."); return; }
      if (selectedType?.require_record_title && !form.record_title.trim()) {
        setError(`Record Title is required for '${selectedType.label}'.`); return;
      }
      if (!file) { setError("Please choose a file to upload."); return; }
      if (!form.no_expiry && form.issue_date && form.expiry_date && form.expiry_date < form.issue_date) {
        setError("Expiry Date cannot be earlier than Issue Date."); return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("document_type_id", form.document_type_id);
      if (form.record_title.trim()) fd.append("record_title", form.record_title.trim());
      fd.append("file", file);
      if (form.issue_date) fd.append("issue_date", form.issue_date);
      if (form.expiry_date && !form.no_expiry) fd.append("expiry_date", form.expiry_date);
      fd.append("no_expiry", form.no_expiry ? "true" : "false");
      if (confirmDuplicate) fd.append("confirm_duplicate", "true");
      await api.post("/company-documents", fd);
      if (docPage !== 1) setDocPage(1); else loadDocuments(); // page 1 shows newest-first by default
      loadCategories();
      resetForm();
      setShowAddForm(false);
      flash("Document saved.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicate_warning) {
        setDupWarning({ message: detail.message });
      } else {
        setError(typeof detail === "string" ? detail : "Failed to save this document.");
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDuplicateSave = () => {
    setDupWarning(null);
    saveDocument(true);
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.record_title || doc.document_type_label}" (${doc.doc_code})? This cannot be undone.`)) return;
    try {
      await api.delete(`/company-documents/${doc.id}`);
      // Drop it from local state instead of re-fetching the whole list --
      // loadDocuments() flips loading back on and re-renders the skeleton,
      // which reads as the page "refreshing" for a one-row change.
      if (documents.length === 1 && docPage > 1) {
        setDocPage(p => p - 1); // last row on a later page -- step back instead of showing an empty page
      } else {
        setDocuments(prev => prev.filter(d => d.id !== doc.id));
      }
      setDocTotal(t => Math.max(0, t - 1));
      loadCategories();
      flash("Document deleted.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete this document.");
    }
  };

  const loadVersions = (docId) => {
    setLoadingVersions(true);
    api.get(`/company-documents/${docId}/versions`)
      .then(r => setVersionsByDoc(prev => ({ ...prev, [docId]: r.data.versions || [] })))
      .catch(() => {})
      .finally(() => setLoadingVersions(false));
  };

  const resetRenewForm = () => {
    setRenewFile(null); setRenewIssueDate(""); setRenewExpiryDate(""); setRenewNoExpiry(false);
    setRenewDateNeedsReview({ issue: false, expiry: false });
    setRenewFileInputKey(k => k + 1);
  };

  const toggleHistory = (doc) => {
    if (expandedDocId === doc.id) { setExpandedDocId(null); return; }
    setExpandedDocId(doc.id);
    resetRenewForm();
    if (!versionsByDoc[doc.id]) loadVersions(doc.id);
  };

  // Same OCR-then-confirm pre-fill as the initial upload (Section 7) --
  // "Upload Latest Copy" is explicitly the same upload flow (VER-06).
  const handleRenewFileChosen = async (chosenFile) => {
    setRenewFile(chosenFile);
    setRenewDateNeedsReview({ issue: false, expiry: false });
    if (!chosenFile) return;
    setRenewReadingDates(true);
    try {
      const fd = new FormData();
      fd.append("file", chosenFile);
      const res = await api.post("/company-documents/ocr-dates", fd);
      if (res.data.issue_date) setRenewIssueDate(res.data.issue_date);
      if (res.data.expiry_date) setRenewExpiryDate(res.data.expiry_date);
      setRenewDateNeedsReview({ issue: !res.data.issue_date, expiry: !res.data.expiry_date });
    } catch {
      // Manual entry always works (VAL-02) -- a failed read is never an error the user needs to see.
    } finally {
      setRenewReadingDates(false);
    }
  };

  const renewDocument = async (docId) => {
    if (!renewFile) { setError("Please choose a file to upload."); return; }
    if (!renewNoExpiry && renewIssueDate && renewExpiryDate && renewExpiryDate < renewIssueDate) {
      setError("Expiry Date cannot be earlier than Issue Date."); return;
    }
    setRenewSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", renewFile);
      if (renewIssueDate) fd.append("issue_date", renewIssueDate);
      if (renewExpiryDate && !renewNoExpiry) fd.append("expiry_date", renewExpiryDate);
      fd.append("no_expiry", renewNoExpiry ? "true" : "false");
      const res = await api.post(`/company-documents/${docId}/renew`, fd);
      loadVersions(docId);
      loadCategories();
      const updated = await api.get(`/company-documents/${docId}`);
      setDocuments(prev => prev.map(d => d.id === docId ? updated.data : d));
      resetRenewForm();
      if (res.data.promoted) {
        flash("New version saved and is now Current.");
      } else {
        // VER-08: not a failure -- the upload succeeded and is in the
        // history, it just didn't become Current. Worth a clear heads-up,
        // not a red error banner.
        setRenewNotice({ title: "Saved to Version History", message: res.data.warning, tone: "info" });
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to upload the new version.");
    } finally {
      setRenewSaving(false);
    }
  };

  const viewVersionFile = async (docId, versionId) => {
    try {
      const res = await api.get(`/company-documents/${docId}/versions/${versionId}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      setError("Could not load this version.");
    }
  };

  const addType = async () => {
    if (!newTypeLabel.trim()) { setError("Please enter a name for the new document type."); return; }
    setSavingType(true);
    setError("");
    try {
      await api.post("/company-documents/types", {
        label: newTypeLabel.trim(), allow_multiple_records: newTypeAllowMultiple, require_record_title: newTypeRequireTitle,
      });
      loadTypes();
      loadCategories();
      setNewTypeLabel(""); setNewTypeAllowMultiple(true); setNewTypeRequireTitle(false);
      setShowAddType(false);
      flash("Document type added.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add this document type.");
    } finally {
      setSavingType(false);
    }
  };

  const viewFile = async (documentId) => {
    try {
      const res = await api.get(`/company-documents/${documentId}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      setError("Could not load this document.");
    }
  };

  // Section 11's category-view actions: View Records switches to the
  // Documents tab filtered to this category; Add New Record switches there
  // with the Add form pre-selected and open.
  const viewCategoryRecords = (cat) => {
    setTypeFilter(cat.id);
    setActiveSubTab("documents");
  };

  const addRecordForCategory = (cat) => {
    setForm({ ...BLANK_FORM, document_type_id: cat.id });
    setShowAddForm(true);
    setActiveSubTab("documents");
  };

  // Status cards at the top of the page: click one to jump straight to the
  // filtered view that explains it.
  const goToDocumentsWithStatus = (status) => {
    setStatusFilter(status);
    setActiveSubTab("documents");
  };

  // Reminder Settings (Section 16, SET-01 to SET-06).
  const addRecipient = async () => {
    const email = newRecipientEmail.trim();
    if (!email) { setError("Please enter an email address."); return; }
    setSavingRecipient(true);
    setError("");
    try {
      await api.post("/company-documents/reminder-recipients", { email });
      loadRecipients();
      setNewRecipientEmail("");
      flash("Recipient added.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add recipient.");
    } finally {
      setSavingRecipient(false);
    }
  };

  const toggleRecipientActive = async (recipient) => {
    try {
      await api.patch(`/company-documents/reminder-recipients/${recipient.id}`, { is_active: !recipient.is_active });
      loadRecipients();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update recipient.");
    }
  };

  const removeRecipient = async (recipient) => {
    if (!window.confirm(`Remove "${recipient.email}" from reminder recipients?`)) return;
    try {
      await api.delete(`/company-documents/reminder-recipients/${recipient.id}`);
      setRecipients(prev => prev.filter(r => r.id !== recipient.id));
      flash("Recipient removed.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove recipient.");
    }
  };

  const sendTestEmail = async () => {
    setSendingTestEmail(true);
    setError("");
    try {
      const body = testEmailAddress.trim() ? { email: testEmailAddress.trim() } : {};
      const res = await api.post("/company-documents/reminder-recipients/test-email", body);
      flash(`Test email sent to ${res.data.sent_to.join(", ")}.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send test email.");
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Not a requirement-document item -- a read-only look at what's due today,
  // never sends anything. Added after real testing showed "Run Compliance
  // Check Now" sending real mail was surprising when someone just wanted to
  // see what would happen.
  const previewDueDocuments = async () => {
    setPreviewing(true);
    setError("");
    try {
      const res = await api.get("/company-documents/reminders/preview");
      setPreviewDue(res.data.due || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to preview due documents.");
    } finally {
      setPreviewing(false);
    }
  };

  // The real action -- sends actual reminder emails for whatever is due
  // today, exactly like the scheduled daily run would.
  const runComplianceCheckNow = async () => {
    setRunningNow(true);
    setError("");
    try {
      const res = await api.post("/company-documents/reminders/run-now");
      loadReminders();
      loadDocuments();
      setPreviewDue(null);
      flash(`Checked ${res.data.documents_due} due document${res.data.documents_due === 1 ? "" : "s"} — ${res.data.sent} sent, ${res.data.failed} failed, ${res.data.skipped_no_recipients} skipped (no recipients).`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to run the compliance check.");
    } finally {
      setRunningNow(false);
    }
  };

  // Business Credentials (Milestone 6, Section 18-19).
  const addCredential = async () => {
    if (!credentialForm.title.trim()) { setError("Please enter a title."); return; }
    if (!credentialForm.password) { setError("Please enter a password."); return; }
    setSavingCredential(true);
    setError("");
    try {
      await api.post("/company-credentials", {
        title: credentialForm.title.trim(),
        username: credentialForm.username.trim() || null,
        password: credentialForm.password,
        notes: credentialForm.notes.trim() || null,
      });
      loadCredentials();
      setCredentialForm({ title: "", username: "", password: "", notes: "" });
      setShowAddCredentialForm(false);
      flash("Credential saved.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save credential.");
    } finally {
      setSavingCredential(false);
    }
  };

  const startEditCredential = (cred) => {
    setEditingCredentialId(cred.id);
    setEditCredentialForm({ title: cred.title, username: cred.username || "", password: "", notes: cred.notes || "" });
  };

  const saveCredentialEdit = async (id) => {
    if (!editCredentialForm.title.trim()) { setError("Please enter a title."); return; }
    setSavingCredentialEdit(true);
    setError("");
    try {
      const body = {
        title: editCredentialForm.title.trim(),
        username: editCredentialForm.username.trim() || null,
        notes: editCredentialForm.notes.trim() || null,
      };
      if (editCredentialForm.password) body.password = editCredentialForm.password; // blank = leave unchanged
      await api.patch(`/company-credentials/${id}`, body);
      loadCredentials();
      setEditingCredentialId(null);
      flash("Credential updated.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update credential.");
    } finally {
      setSavingCredentialEdit(false);
    }
  };

  const toggleArchiveCredential = async (cred) => {
    try {
      await api.patch(`/company-credentials/${cred.id}`, { is_archived: !cred.is_archived });
      loadCredentials();
      flash(cred.is_archived ? "Credential unarchived." : "Credential archived.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update credential.");
    }
  };

  const deleteCredential = async (cred) => {
    if (!window.confirm(`Permanently delete "${cred.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/company-credentials/${cred.id}`);
      setCredentials(prev => prev.filter(c => c.id !== cred.id));
      flash("Credential deleted.");
    } catch (e) {
      // 403 here means the signed-in user isn't an org admin -- permanent
      // delete is deliberately restricted (SEC-07), not a bug.
      setError(e.response?.data?.detail || "Failed to delete credential.");
    }
  };

  // Reveal keeps the password visible inline until Hide is clicked; Copy
  // never displays it, just writes to the clipboard -- Section 19 (SEC-04)
  // treats these as two distinct, separately audited actions on the backend.
  const revealPassword = async (id) => {
    setRevealingId(id);
    setError("");
    try {
      const res = await api.post(`/company-credentials/${id}/reveal`, { action: "reveal" });
      setRevealedPasswords(prev => ({ ...prev, [id]: res.data.password }));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reveal password.");
    } finally {
      setRevealingId(null);
    }
  };

  const hidePassword = (id) => {
    setRevealedPasswords(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  const copyPassword = async (id) => {
    setError("");
    try {
      const res = await api.post(`/company-credentials/${id}/reveal`, { action: "copy" });
      await navigator.clipboard.writeText(res.data.password);
      flash("Password copied to clipboard.");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to copy password.");
    }
  };

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" />
      <style>{CSS}</style>
      <header className="adm-topbar">
        <button className="adm-back" onClick={() => navigate("/")} title="Back"><IconArrowLeft /></button>
        <div className="adm-brand">
          <div className="adm-brand-mark">🗂️</div>
          <span className="adm-brand-label">HRMS & Payroll — Company Documents</span>
        </div>
        <div style={{ marginRight: 8, fontSize: 12, color: "#6080aa" }}>{user?.org_name}</div>
        <div className="adm-avatar" title={user?.name}>{initials}</div>
      </header>

      <div className="adm-tabs">
        <button className="adm-tab" onClick={() => navigate("/hrms/dashboard")}>Dashboard</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/employees")}>Employees</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/payroll")}>Payroll</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/expiry")}>Document Expiry</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/data-quality")}>Data Quality</button>
        <button className="adm-tab active">Company Documents</button>
        <button className="adm-tab" onClick={() => navigate("/hrms/assistant")}>AI Assistant</button>
      </div>

      <div className="adm-main">
        {error && <div className="adm-error">{error}</div>}
        {msg && <div className="adm-toast"><span className="adm-toast-dot" />{msg}</div>}

        <div className="cdoc-page-head">
          <div>
            <h1>Company Documents</h1>
            <p>Commercial Registration, licences, certificates and other company-level documents — separate from individual employee records.</p>
          </div>
        </div>

        {!loadingCategories && (
          <div className="cdoc-stats-grid">
            {statusSummary.expired > 0 && (
              <button type="button" className="cdoc-stat-card expired" onClick={() => goToDocumentsWithStatus("expired")}>
                <span className="count">{statusSummary.expired}</span>
                <span className="label">Expired</span>
              </button>
            )}
            {statusSummary.expiring_soon > 0 && (
              <button type="button" className="cdoc-stat-card expiring" onClick={() => goToDocumentsWithStatus("expiring_soon")}>
                <span className="count">{statusSummary.expiring_soon}</span>
                <span className="label">Expiring Soon</span>
              </button>
            )}
            {statusSummary.review_required > 0 && (
              <button type="button" className="cdoc-stat-card review" onClick={() => goToDocumentsWithStatus("review_required")}>
                <span className="count">{statusSummary.review_required}</span>
                <span className="label">Review Required</span>
              </button>
            )}
            {statusSummary.valid > 0 && (
              <button type="button" className="cdoc-stat-card ok" onClick={() => goToDocumentsWithStatus("valid")}>
                <span className="count">{statusSummary.valid}</span>
                <span className="label">Valid</span>
              </button>
            )}
            {statusSummary.no_expiry > 0 && (
              <button type="button" className="cdoc-stat-card noexpiry" onClick={() => goToDocumentsWithStatus("no_expiry")}>
                <span className="count">{statusSummary.no_expiry}</span>
                <span className="label">No Expiry</span>
              </button>
            )}
          </div>
        )}

        <div className="cdoc-subtabs">
          <button type="button" className={`cdoc-subtab${activeSubTab === "documents" ? " active" : ""}`} onClick={() => setActiveSubTab("documents")}>Documents</button>
          <button type="button" className={`cdoc-subtab${activeSubTab === "categories" ? " active" : ""}`} onClick={() => setActiveSubTab("categories")}>Document Categories</button>
          <button type="button" className={`cdoc-subtab${activeSubTab === "reminders" ? " active" : ""}`} onClick={() => setActiveSubTab("reminders")}>Reminder Settings</button>
          <button type="button" className={`cdoc-subtab${activeSubTab === "credentials" ? " active" : ""}`} onClick={() => setActiveSubTab("credentials")}>Business Credentials</button>
        </div>

        {activeSubTab === "documents" && (
        <>
        <div className="adm-card" id="cdoc-add-document">
          <div className="adm-card-title">Add a Document</div>
          {!showAddForm ? (
            <button type="button" className="adm-action-btn" onClick={() => setShowAddForm(true)}>+ Add Document</button>
          ) : (
            <div>
              <div className="adm-form-grid">
                <div className="adm-field">
                  <label className="adm-label">Document Type *</label>
                  <select className="adm-select" value={form.document_type_id}
                          onChange={e => setForm(p => ({ ...p, document_type_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                {selectedType?.allow_multiple_records && (
                  <div className="adm-field">
                    <label className="adm-label">
                      {selectedType.label === "Company Director ID Card" ? "Director Name" : "Record Title"}
                      {selectedType.require_record_title ? " *" : ""}
                    </label>
                    <input className="adm-input" value={form.record_title}
                           onChange={e => setForm(p => ({ ...p, record_title: e.target.value }))}
                           placeholder={selectedType.label === "Company Director ID Card" ? "e.g. Ahmed Al Balushi" : "e.g. CCTV Installation Activity Licence"} />
                  </div>
                )}
                <div className="adm-field">
                  <label className="adm-label">File *</label>
                  <input key={fileInputKey} className="adm-input" type="file" accept=".pdf,.jpg,.jpeg,.png"
                         onChange={e => handleFileChosen(e.target.files[0] || null)} />
                  {readingDates && <div style={{ fontSize: 11, color: "#0284c7", marginTop: 6 }}>Reading document…</div>}
                </div>
                <div className="adm-field">
                  <label className="adm-label">Issue Date {dateNeedsReview.issue && <span style={{ color: "#b45309", textTransform: "none", fontWeight: 500 }}>— couldn't read confidently, please check</span>}</label>
                  <input className="adm-input" type="date" value={form.issue_date}
                         onChange={e => setForm(p => ({ ...p, issue_date: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Expiry Date {dateNeedsReview.expiry && !form.no_expiry && <span style={{ color: "#b45309", textTransform: "none", fontWeight: 500 }}>— couldn't read confidently, please check</span>}</label>
                  <input className="adm-input" type="date" value={form.expiry_date} disabled={form.no_expiry}
                         onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} />
                </div>
                <div className="adm-field">
                  <label className="adm-label">&nbsp;</label>
                  <label className="adm-checkbox-row">
                    <input type="checkbox" checked={form.no_expiry}
                           onChange={e => setForm(p => ({ ...p, no_expiry: e.target.checked, expiry_date: e.target.checked ? "" : p.expiry_date }))} />
                    No Expiry Date
                  </label>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button type="button" className="adm-submit" disabled={saving} onClick={() => saveDocument()}>
                  {saving ? "Saving…" : "Save Document"}
                </button>
                <button type="button" className="adm-action-btn" onClick={() => { setShowAddForm(false); resetForm(); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div className="adm-card" id="cdoc-all-documents">
          <div className="adm-card-title">All Documents ({docTotal})</div>
          <div className="adm-form-grid" style={{ marginBottom: 16 }}>
            <div className="adm-field">
              <label className="adm-label">Search</label>
              <input className="adm-input" placeholder="Search by title or category…"
                     value={searchInput} onChange={e => setSearchInput(e.target.value)} />
            </div>
            <div className="adm-field">
              <label className="adm-label">Filter by Type</label>
              <select className="adm-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All document types</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div className="adm-field">
              <label className="adm-label">Status</label>
              <select className="adm-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="valid">Valid</option>
                <option value="expiring_soon">Expiring Soon</option>
                <option value="expired">Expired</option>
                <option value="no_expiry">No Expiry</option>
                <option value="review_required">Review Required</option>
              </select>
            </div>
            <div className="adm-field">
              <label className="adm-label">Sort By</label>
              <select className="adm-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="">Newest First</option>
                <option value="expiry_date">Expiry Date</option>
                <option value="title">Title</option>
                <option value="latest_upload">Latest Upload</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div>
              {[0, 1, 2, 3].map(i => (
                <div className="adm-skeleton-row" key={i}>
                  <div className="adm-skeleton-bar" style={{ width: "60%" }} />
                  <div className="adm-skeleton-bar" style={{ width: "75%" }} />
                  <div className="adm-skeleton-bar" style={{ width: "50%" }} />
                  <div className="adm-skeleton-bar" style={{ width: "50%" }} />
                  <div className="adm-skeleton-bar" style={{ width: "40%" }} />
                </div>
              ))}
            </div>
          ) : documents.length === 0 ? (
            <div className="adm-empty">No company documents yet — add the first one above.</div>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr><th>Doc Code</th><th>Type</th><th>Record Title</th><th>Issue Date</th><th>Expiry Date</th><th>Status</th><th>File</th><th></th></tr>
                </thead>
                <tbody>
                  {documents.map(d => (
                    <Fragment key={d.id}>
                      <tr>
                        <td className="doc-code">{d.doc_code}</td>
                        <td className="doc-title">{d.document_type_label}</td>
                        <td>{d.record_title || "—"}</td>
                        <td className="cdoc-days">{d.current_version?.issue_date || "—"}</td>
                        <td className="cdoc-days">{d.current_version?.no_expiry ? "No Expiry" : (d.current_version?.expiry_date || "—")}</td>
                        <td><StatusBadge status={d.status} /></td>
                        <td>{d.current_version?.file_name || "—"}</td>
                        <td>
                          <div className="cdoc-row-actions">
                            <button type="button" className="adm-icon-btn" title="View file" onClick={() => viewFile(d.id)}><IconEye /></button>
                            <button type="button" className={`adm-icon-btn${expandedDocId === d.id ? " danger" : ""}`}
                                    title="Version history & renew" onClick={() => toggleHistory(d)}>
                              <IconHistory />
                            </button>
                            <button type="button" className="adm-icon-btn danger" title="Delete record" onClick={() => deleteDocument(d)}><IconTrash /></button>
                          </div>
                        </td>
                      </tr>
                      {expandedDocId === d.id && (
                        <tr className="cdoc-history-row">
                          <td colSpan={8}>
                            <div className="cdoc-history-panel">
                              <div>
                                <div className="cdoc-history-section-title">Upload Latest Copy</div>
                                <div className="cdoc-renew-form">
                                  <div className="cdoc-renew-field" style={{ minWidth: 220 }}>
                                    <label className="adm-label">File *</label>
                                    <input key={renewFileInputKey} className="adm-input" type="file" accept=".pdf,.jpg,.jpeg,.png"
                                           onChange={e => handleRenewFileChosen(e.target.files[0] || null)} />
                                    {renewReadingDates && <div style={{ fontSize: 11, color: "#0284c7", marginTop: 6 }}>Reading document…</div>}
                                  </div>
                                  <div className="cdoc-renew-field">
                                    <label className="adm-label">Issue Date {renewDateNeedsReview.issue && <span style={{ color: "#b45309", textTransform: "none", fontWeight: 500 }}>— check</span>}</label>
                                    <input className="adm-input" type="date" value={renewIssueDate} onChange={e => setRenewIssueDate(e.target.value)} />
                                  </div>
                                  <div className="cdoc-renew-field">
                                    <label className="adm-label">Expiry Date {renewDateNeedsReview.expiry && !renewNoExpiry && <span style={{ color: "#b45309", textTransform: "none", fontWeight: 500 }}>— check</span>}</label>
                                    <input className="adm-input" type="date" value={renewExpiryDate} disabled={renewNoExpiry}
                                           onChange={e => setRenewExpiryDate(e.target.value)} />
                                  </div>
                                  <label className="adm-checkbox-row" style={{ paddingBottom: 9 }}>
                                    <input type="checkbox" checked={renewNoExpiry}
                                           onChange={e => setRenewNoExpiry(e.target.checked)} />
                                    No Expiry Date
                                  </label>
                                  <button type="button" className="adm-submit" style={{ padding: "9px 18px" }}
                                          disabled={renewSaving} onClick={() => renewDocument(d.id)}>
                                    {renewSaving ? "Uploading…" : "Upload New Version"}
                                  </button>
                                </div>
                              </div>

                              <div>
                                <div className="cdoc-history-section-title">Version History ({d.version_count ?? 1})</div>
                                {loadingVersions && !versionsByDoc[d.id] ? (
                                  <div className="adm-skeleton-bar" style={{ width: "40%", height: 12 }} />
                                ) : (
                                  <div className="cdoc-version-list">
                                    {(versionsByDoc[d.id] || []).map(v => (
                                      <div className="cdoc-version-row" key={v.id}>
                                        <span className="cdoc-version-num">v{v.version_number}</span>
                                        <span className={`cdoc-version-badge ${v.is_current ? "current" : "historical"}`}>
                                          {v.is_current ? "Current" : "Historical"}
                                        </span>
                                        <StatusBadge status={v.status} />
                                        <span>{v.issue_date || "—"}</span>
                                        <span>{v.no_expiry ? "No Expiry" : (v.expiry_date || "—")}</span>
                                        <span className="cdoc-version-meta">{v.uploaded_by_name || "—"} · {new Date(v.created_at).toLocaleDateString()}</span>
                                        <span className="cdoc-version-meta">{v.file_name}</span>
                                        <button type="button" className="adm-icon-btn" title="View this version"
                                                onClick={() => viewVersionFile(d.id, v.id)}><IconEye /></button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && docTotalPages > 1 && (
            <div className="cdoc-pagination">
              <button type="button" className="adm-action-btn" disabled={docPage <= 1}
                      onClick={() => setDocPage(p => p - 1)}>
                ← Previous
              </button>
              <span className="cdoc-pagination-page">Page {docPage} of {docTotalPages}</span>
              <button type="button" className="adm-action-btn" disabled={docPage >= docTotalPages}
                      onClick={() => setDocPage(p => p + 1)}>
                Next →
              </button>
            </div>
          )}
        </div>
        </>
        )}

        {activeSubTab === "categories" && (
        <div className="adm-card">
          <div className="adm-card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Document Categories</span>
            {!showAddType && (
              <button type="button" className="adm-action-btn" onClick={() => setShowAddType(true)}>+ Add Custom Type</button>
            )}
          </div>
          <div className="adm-card-desc">29 predefined types are provided out of the box — administrators can add more without a software change.</div>
          {showAddType && (
            <div className="adm-form-grid" style={{ marginBottom: 16 }}>
              <div className="adm-field">
                <label className="adm-label">Type Name *</label>
                <input className="adm-input" value={newTypeLabel} onChange={e => setNewTypeLabel(e.target.value)} placeholder="e.g. Chamber of Commerce Letter" />
              </div>
              <div className="adm-field">
                <label className="adm-label">&nbsp;</label>
                <label className="adm-checkbox-row">
                  <input type="checkbox" checked={newTypeAllowMultiple} onChange={e => setNewTypeAllowMultiple(e.target.checked)} />
                  Allow Multiple Records
                </label>
              </div>
              <div className="adm-field">
                <label className="adm-label">&nbsp;</label>
                <label className="adm-checkbox-row">
                  <input type="checkbox" checked={newTypeRequireTitle} onChange={e => setNewTypeRequireTitle(e.target.checked)} />
                  Require Record Title
                </label>
              </div>
              <div className="adm-field" style={{ justifyContent: "flex-end", flexDirection: "row", gap: 10, alignItems: "flex-end" }}>
                <button type="button" className="adm-submit" style={{ padding: "9px 18px" }} disabled={savingType} onClick={addType}>
                  {savingType ? "Adding…" : "Add Type"}
                </button>
                <button type="button" className="adm-action-btn" onClick={() => setShowAddType(false)}>Cancel</button>
              </div>
            </div>
          )}

          {loadingCategories ? (
            <div className="adm-skeleton-bar" style={{ width: "45%" }} />
          ) : (() => {
            const inUse = categories.filter(c => c.record_count > 0);
            const visible = showAllCategories ? categories : inUse;
            const unusedCount = categories.length - inUse.length;
            return (
              <>
                {visible.length === 0 ? (
                  <div className="adm-empty">No document categories in use yet — add your first document from the Documents tab.</div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr><th>Document Category</th><th>Records</th><th>Overall Status</th><th></th></tr>
                      </thead>
                      <tbody>
                        {visible.map(c => (
                          <tr key={c.id}>
                            <td className="doc-title">{c.label}</td>
                            <td className="cdoc-days">{c.record_count}</td>
                            <td><StatusBadge status={c.overall_status} /></td>
                            <td>
                              <div className="cdoc-row-actions">
                                {c.record_count > 0 && (
                                  <button type="button" className="adm-action-btn" onClick={() => viewCategoryRecords(c)}>View Records</button>
                                )}
                                <button type="button" className="adm-action-btn" onClick={() => addRecordForCategory(c)}>Add New Record</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {unusedCount > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <button type="button" className="adm-action-btn" onClick={() => setShowAllCategories(s => !s)}>
                      {showAllCategories ? "Show Fewer" : `Show All ${categories.length} Document Types (${unusedCount} unused)`}
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
        )}

        {activeSubTab === "reminders" && (
        <>
        <div className="adm-card">
          <div className="adm-card-title">Reminder Recipients</div>
          <div className="adm-card-desc">
            These addresses receive daily reminder emails for expiring, expired or unresolved company documents (Section 16).
            Everyone on the platform sends from the same shared address — you only manage who receives your company's reminders.
          </div>

          <div className="cdoc-renew-form" style={{ marginBottom: 16 }}>
            <div className="cdoc-renew-field" style={{ minWidth: 260 }}>
              <label className="adm-label">Add Recipient</label>
              <input className="adm-input" type="email" placeholder="hr@yourcompany.com"
                     value={newRecipientEmail} onChange={e => setNewRecipientEmail(e.target.value)}
                     onKeyDown={e => { if (e.key === "Enter") addRecipient(); }} />
            </div>
            <button type="button" className="adm-submit" style={{ padding: "9px 18px" }} disabled={savingRecipient} onClick={addRecipient}>
              {savingRecipient ? "Adding…" : "+ Add"}
            </button>
          </div>

          {loadingRecipients ? (
            <div className="adm-skeleton-bar" style={{ width: "40%" }} />
          ) : recipients.length === 0 ? (
            <div className="adm-empty">No reminder recipients yet — add one above.</div>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead><tr><th>Email</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {recipients.map(r => (
                    <tr key={r.id}>
                      <td>{r.email}</td>
                      <td>
                        <span className={`cdoc-status-badge ${r.is_active ? "valid" : "norecords"}`}>
                          {r.is_active ? "Active" : "Deactivated"}
                        </span>
                      </td>
                      <td>
                        <div className="cdoc-row-actions">
                          <button type="button" className="adm-action-btn" onClick={() => toggleRecipientActive(r)}>
                            {r.is_active ? "Deactivate" : "Reactivate"}
                          </button>
                          <button type="button" className="adm-action-btn danger" onClick={() => removeRecipient(r)}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Send Test Email (SET-06) -- hidden per user request for now, revisit later.
        Left in place, commented out, rather than deleted, so re-enabling it is a one-line change. */}
        {false && (
        <div className="adm-card">
          <div className="adm-card-title">Send Test Email</div>
          <div className="adm-card-desc">
            Sends a generic test message to confirm your email setup works — it's not tied to any real document, so it works even on a day nothing is due.
          </div>
          <div className="cdoc-diagnostic-action">
            <input className="adm-input" type="email" placeholder="someone@yourcompany.com" style={{ flex: 1, minWidth: 220, maxWidth: 360 }}
                   value={testEmailAddress} onChange={e => setTestEmailAddress(e.target.value)} />
            <button type="button" className="adm-action-btn" disabled={sendingTestEmail} onClick={sendTestEmail}>
              {sendingTestEmail ? "Sending…" : "Send Test Email"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>Leave the address blank to test every active recipient instead.</div>
        </div>
        )}

        <div className="adm-card">
          <div className="adm-card-title">Compliance Check</div>
          <div className="adm-card-desc">
            See which documents are due for a reminder today, or send them immediately instead of waiting for the scheduled daily run.
          </div>
          <div className="cdoc-diagnostic-action" style={{ marginBottom: previewDue !== null ? 16 : 0 }}>
            <button type="button" className="adm-action-btn" disabled={previewing} onClick={previewDueDocuments}>
              {previewing ? "Checking…" : "Preview Due Documents"}
            </button>
            <button type="button" className="adm-submit" style={{ padding: "9px 18px" }} disabled={runningNow} onClick={runComplianceCheckNow}>
              {runningNow ? "Sending…" : "Send Reminders Now"}
            </button>
          </div>

          {previewDue !== null && (
            previewDue.length === 0 ? (
              <div className="adm-empty">No documents are due for a reminder today.</div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead><tr><th>Doc Code</th><th>Type</th><th>Record</th><th>Days</th><th>Status</th></tr></thead>
                  <tbody>
                    {previewDue.map(d => (
                      <tr key={d.document_id}>
                        <td className="doc-code">{d.doc_code}</td>
                        <td className="doc-title">{d.document_type_label}</td>
                        <td>{d.record_title || "—"}</td>
                        <td className="cdoc-days">{d.days_to_expiry >= 0 ? `${d.days_to_expiry} left` : `${-d.days_to_expiry} overdue`}</td>
                        <td><StatusBadge status={d.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        <div className="adm-card">
          <div className="adm-card-title">Reminder History</div>
          <div className="adm-card-desc">
            The AI Compliance Manager checks every document once a day and logs what it found here — even when a reminder had no one to send to.
          </div>
          {loadingReminders ? (
            <div className="adm-skeleton-bar" style={{ width: "40%" }} />
          ) : remindersLog.length === 0 ? (
            <div className="adm-empty">No reminders logged yet.</div>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead><tr><th>Date</th><th>Type</th><th>Record</th><th>Days</th><th>Recipients</th><th>Status</th></tr></thead>
                <tbody>
                  {remindersLog.map(r => (
                    <tr key={r.id}>
                      <td className="cdoc-days">{r.reminder_date}</td>
                      <td className="doc-title">{r.document_type_label}</td>
                      <td>{r.record_title || "—"}</td>
                      <td className="cdoc-days">{r.days_to_expiry >= 0 ? `${r.days_to_expiry} left` : `${-r.days_to_expiry} overdue`}</td>
                      <td>{r.recipients.length > 0 ? r.recipients.join(", ") : "—"}</td>
                      <td><StatusBadge status={r.delivery_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {activeSubTab === "credentials" && (
        <>
        <div className="adm-card">
          <div className="adm-card-title">Add a Credential</div>
          <div className="adm-card-desc">
            Login details for online bidding portals or other company systems (Section 18). Passwords are encrypted — only an organisation admin can reveal or copy the real value.
          </div>
          {!showAddCredentialForm ? (
            <button type="button" className="adm-action-btn" onClick={() => setShowAddCredentialForm(true)}>+ Add Credential</button>
          ) : (
            <div>
              <div className="adm-form-grid">
                <div className="adm-field">
                  <label className="adm-label">Title *</label>
                  <input className="adm-input" value={credentialForm.title}
                         onChange={e => setCredentialForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Tender Board Portal" />
                </div>
                <div className="adm-field">
                  <label className="adm-label">User Name</label>
                  <input className="adm-input" value={credentialForm.username}
                         onChange={e => setCredentialForm(p => ({ ...p, username: e.target.value }))} placeholder="e.g. acme_bidder" />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Password *</label>
                  <input className="adm-input" type="password" autoComplete="new-password" value={credentialForm.password}
                         onChange={e => setCredentialForm(p => ({ ...p, password: e.target.value }))} />
                </div>
                <div className="adm-field" style={{ gridColumn: "1 / -1" }}>
                  <label className="adm-label">Notes</label>
                  <input className="adm-input" value={credentialForm.notes}
                         onChange={e => setCredentialForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional — non-password instructions or account information" />
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button type="button" className="adm-submit" disabled={savingCredential} onClick={addCredential}>
                  {savingCredential ? "Saving…" : "Save Credential"}
                </button>
                <button type="button" className="adm-action-btn"
                        onClick={() => { setShowAddCredentialForm(false); setCredentialForm({ title: "", username: "", password: "", notes: "" }); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div className="adm-card">
          <div className="adm-card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>All Credentials ({credentials.filter(c => showArchivedCredentials || !c.is_archived).length})</span>
            <label className="adm-checkbox-row" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={showArchivedCredentials} onChange={e => setShowArchivedCredentials(e.target.checked)} />
              Show Archived
            </label>
          </div>
          {loadingCredentials ? (
            <div className="adm-skeleton-bar" style={{ width: "40%" }} />
          ) : (() => {
            const visible = credentials.filter(c => showArchivedCredentials || !c.is_archived);
            return visible.length === 0 ? (
              <div className="adm-empty">No credentials yet — add one above.</div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead><tr><th>Title</th><th>User Name</th><th>Password</th><th>Notes</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {visible.map(cred => (
                      <Fragment key={cred.id}>
                        <tr>
                          <td className="doc-title">{cred.title}</td>
                          <td>{cred.username || "—"}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontFamily: "monospace", letterSpacing: revealedPasswords[cred.id] ? "normal" : "2px" }}>
                                {revealedPasswords[cred.id] || "••••••••"}
                              </span>
                              {revealedPasswords[cred.id] ? (
                                <button type="button" className="adm-action-btn" onClick={() => hidePassword(cred.id)}>Hide</button>
                              ) : (
                                <button type="button" className="adm-action-btn" disabled={revealingId === cred.id} onClick={() => revealPassword(cred.id)}>
                                  {revealingId === cred.id ? "Revealing…" : "Reveal"}
                                </button>
                              )}
                              <button type="button" className="adm-action-btn" onClick={() => copyPassword(cred.id)}>Copy</button>
                            </div>
                          </td>
                          <td>{cred.notes || "—"}</td>
                          <td>
                            <span className={`cdoc-status-badge ${cred.is_archived ? "norecords" : "valid"}`}>
                              {cred.is_archived ? "Archived" : "Active"}
                            </span>
                          </td>
                          <td>
                            <div className="cdoc-row-actions">
                              <button type="button" className="adm-action-btn" onClick={() => startEditCredential(cred)}>Edit</button>
                              <button type="button" className="adm-action-btn" onClick={() => toggleArchiveCredential(cred)}>
                                {cred.is_archived ? "Unarchive" : "Archive"}
                              </button>
                              <button type="button" className="adm-action-btn danger" onClick={() => deleteCredential(cred)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                        {editingCredentialId === cred.id && (
                          <tr className="cdoc-history-row">
                            <td colSpan={6}>
                              <div className="adm-form-grid">
                                <div className="adm-field">
                                  <label className="adm-label">Title *</label>
                                  <input className="adm-input" value={editCredentialForm.title}
                                         onChange={e => setEditCredentialForm(p => ({ ...p, title: e.target.value }))} />
                                </div>
                                <div className="adm-field">
                                  <label className="adm-label">User Name</label>
                                  <input className="adm-input" value={editCredentialForm.username}
                                         onChange={e => setEditCredentialForm(p => ({ ...p, username: e.target.value }))} />
                                </div>
                                <div className="adm-field">
                                  <label className="adm-label">New Password</label>
                                  <input className="adm-input" type="password" autoComplete="new-password" placeholder="Leave blank to keep unchanged"
                                         value={editCredentialForm.password} onChange={e => setEditCredentialForm(p => ({ ...p, password: e.target.value }))} />
                                </div>
                                <div className="adm-field" style={{ gridColumn: "1 / -1" }}>
                                  <label className="adm-label">Notes</label>
                                  <input className="adm-input" value={editCredentialForm.notes}
                                         onChange={e => setEditCredentialForm(p => ({ ...p, notes: e.target.value }))} />
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                                <button type="button" className="adm-submit" style={{ padding: "9px 18px" }} disabled={savingCredentialEdit}
                                        onClick={() => saveCredentialEdit(cred.id)}>
                                  {savingCredentialEdit ? "Saving…" : "Save"}
                                </button>
                                <button type="button" className="adm-action-btn" onClick={() => setEditingCredentialId(null)}>Cancel</button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
        </>
        )}
      </div>

      {dupWarning && (
        <div className="cdoc-modal-overlay" onClick={() => setDupWarning(null)}>
          <div className="cdoc-modal" onClick={e => e.stopPropagation()} role="alertdialog" aria-labelledby="cdoc-dup-title">
            <div className="cdoc-modal-icon"><IconWarning /></div>
            <div className="cdoc-modal-title" id="cdoc-dup-title">Possible Duplicate Record</div>
            <div className="cdoc-modal-body">{dupWarning.message}</div>
            <div className="cdoc-modal-actions">
              <button type="button" className="adm-action-btn" onClick={() => setDupWarning(null)}>Cancel</button>
              <button type="button" className="adm-submit cdoc-modal-confirm" onClick={confirmDuplicateSave}>Add Anyway</button>
            </div>
          </div>
        </div>
      )}

      {renewNotice && (
        <div className="cdoc-modal-overlay" onClick={() => setRenewNotice(null)}>
          <div className="cdoc-modal" onClick={e => e.stopPropagation()} role="alertdialog" aria-labelledby="cdoc-renew-title">
            <div className="cdoc-modal-icon"><IconWarning /></div>
            <div className="cdoc-modal-title" id="cdoc-renew-title">{renewNotice.title}</div>
            <div className="cdoc-modal-body">{renewNotice.message}</div>
            <div className="cdoc-modal-actions">
              <button type="button" className="adm-submit" onClick={() => setRenewNotice(null)}>Got It</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
